import { Area, Freeze, Viewport } from '../../types';
import { getSizeForViewportIndex } from '../../utils/viewport/viewportUtils';
import { getAfterFreezeX, getSheetCellsLeft } from '../sheetRTLHelper';
import { getHiddenRanges } from './cellHidden';
import {
  doAreaIntersectWithViewport,
  generateCombinedViewportAreas,
} from './cellViewHelpers';
import {
  ColumnCellView,
  ColumnsViewConfigs,
  GetCellView,
  ColumnCellsProps,
} from './cellViewTypes';

type Configs = Partial<ColumnsViewConfigs>;
type CellViewCallback = GetCellView<ColumnCellView>;

export function getColumnsCellView(
  props: ColumnCellsProps,
  getCell: CellViewCallback,
  configs: Configs = {}
) {
  const Cells: JSX.Element[] = [];
  const getCells = getColumnCells(props, getCell, configs, Cells);

  getCells();

  const isFrozen = props.freezedCols.frozen;
  if (isFrozen) getCells(true);

  return Cells;
}

// #region get Column cell
const getColumnCells = (
  props: ColumnCellsProps,
  getCell: CellViewCallback,
  configs: Configs,
  Cells: JSX.Element[]
) => {
  const params = getViewParams(props, configs);

  const { isRtl, zoom, defaultColWidth, hiddenCols, maxColIndex, freezedCols } =
    props;

  const { checkHiddenColumns } = configs;

  const mergedCellsInViewport = params.mergedCellsInViewport;
  const renderedMergedCells: Set<string> = new Set();

  return (frozenColumn = false) => {
    const key = frozenColumn ? 'frozen' : '';
    const sizes = params[`${key}Sizes`];
    const indexes = params[`${key}Indexes`];
    let x = params[`${key}X`];

    const getHiddens = checkHiddenColumns
      ? getHiddenRanges(indexes, hiddenCols, maxColIndex, freezedCols)
      : () => undefined;

    // INFO: Normal Loop is Faster than forEach
    const maxIndexes = indexes.length;
    for (let pos = 0; pos < maxIndexes; pos++) {
      const ci = indexes[pos];

      const width = getSizeForViewportIndex(sizes, pos, defaultColWidth, zoom);
      if (isRtl) x -= width;

      const hiddenColumns = getHiddens(pos, ci);

      const cellViewProps: ColumnCellView = {
        x,
        ci,
        width,
        frozenColumn,
        mergedCellsInViewport,
        renderedMergedCells,
        hiddenColumns,
      };

      const ColCells = getCell(cellViewProps);
      if (Array.isArray(ColCells)) {
        Cells.push(...ColCells);
      } else {
        Cells.push(ColCells);
      }

      if (!isRtl) x += width;
    }
  };
};
// #endregion

// #region view helpers
const getViewParams = (props: ColumnCellsProps, configs: Configs) => {
  const {
    colViewport,
    rowViewport,
    gridWidth,
    rowHeaderWidth,
    isRtl,
    freezedCols,
    freezedRows,
    freezedColsSum,
    zoom,
    merges,
  } = props;

  const { frozenIndexes, frozenSizes, nonFrozenIndexes, nonFrozenSizes } =
    colViewport;

  let frozenX = getSheetCellsLeft(gridWidth, rowHeaderWidth, isRtl);

  const X = freezedCols.frozen
    ? getAfterFreezeX(freezedColsSum, frozenX, isRtl, zoom)
    : frozenX;

  let mergedCellsInViewport;
  if (configs.areEditableCells) {
    mergedCellsInViewport = getMergedCellsInViewport(
      colViewport,
      rowViewport,
      freezedCols,
      freezedRows,
      merges
    );
  }

  return {
    frozenIndexes,
    frozenSizes,
    frozenX,
    Indexes: nonFrozenIndexes,
    Sizes: nonFrozenSizes,
    X,
    mergedCellsInViewport,
  };
};

const getMergedCellsInViewport = (
  colViewport: Viewport,
  rowViewport: Viewport,
  freezedCols: Freeze,
  freezedRows: Freeze,
  merges: Area[]
) => {
  const vAreas = generateCombinedViewportAreas(
    colViewport,
    rowViewport,
    freezedCols,
    freezedRows
  );

  return merges.filter((area) =>
    doAreaIntersectWithViewport(freezedCols, freezedRows, area, vAreas)
  );
};
// #endregion
